import { useConvex } from "convex/react";
import { ArrowRight, Camera, Check, MapPin, Navigation, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useEditorActions } from "@/hooks/useEditorActions";
import { useEditorStore } from "@/store/editorStore";
import type { Tables } from "@/types/database";
import { repositionHotspotsForNorthChange } from "@/utils/autoHotspotGenerator";
import { getPanoramaMediaType } from "@/utils/panoramaMedia";
import { movePanoramaToTrashFromUrls } from "@/utils/trashManager";
import { api } from "../../../convex/_generated/api";
import { GPSLocationPicker } from "./GPSLocationPicker";
import { VersionHistory } from "./VersionHistory";

type Panorama = Tables<"panoramas">;
type PanoramaDataRecord = Record<string, unknown>;

interface PanoramaPropertiesPanelProps {
  updatePanoramaImpl?: (panoramaId: string, updates: Partial<Panorama>) => Promise<void>;
  deletePanoramaImpl?: (panoramaId: string) => Promise<void>;
  uploadPanoramaImpl?: (files: FileList, panoramaId?: string) => Promise<void>;
}


export const PanoramaPropertiesPanel = ({
  updatePanoramaImpl,
  deletePanoramaImpl,
  uploadPanoramaImpl,
}: PanoramaPropertiesPanelProps) => {
  const convex = useConvex();
  // Get all state from the store
  const {
    selectedPanorama,
    selectedPanoramaIds,
    currentCameraView,
    defaultViewSaveEvent,
    hotspots,
    panoramas,
    setDefaultViewSaveEvent,
    setHotspots,
  } = useEditorStore();

  // Check for debug URL parameter
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const showDebug = urlParams.get("debug") === "true";

  // TODO: Camera debug data will be re-implemented with new viewer
  // Live camera stats state
  // const [liveCameraData, setLiveCameraData] = useState({
  //   yaw: 0,
  //   pitch: 0,
  //   fov: 75,
  //   position: { x: 0, y: 0, z: 0 },
  //   target: { x: 1, y: 0, z: 0 }
  // });

  // Get actions from custom hook
  const { updatePanoramaFromPanel, deletePanoramaFromPanel } = useEditorActions(
    undefined,
    undefined,
    undefined,
    undefined,
    updatePanoramaImpl,
    deletePanoramaImpl,
  );

  const [showGPSPicker, setShowGPSPicker] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("properties");
  const [localName, setLocalName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [minZoomInput, setMinZoomInput] = useState("0.2");
  const [maxZoomInput, setMaxZoomInput] = useState("8");
  const [defaultViewApproved, setDefaultViewApproved] = useState(false);
  const [defaultViewApprovedNonce, setDefaultViewApprovedNonce] = useState(0);
  const [cameraIconNonce, setCameraIconNonce] = useState(0);
  const savedDefaultViewRef = useRef<{ yaw: number; pitch: number; fov: number } | null>(null);
  const clickSoundRef = useRef<HTMLAudioElement | null>(null);
  const prevDefaultViewApprovedRef = useRef(false);
  const { toast } = useToast();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0 && uploadPanoramaImpl) {
      uploadPanoramaImpl(files, selectedPanorama.id);
    }
  };

  const handleNameChange = (value: string) => {
    setLocalName(value);
  };

  useEffect(() => {
    if (!selectedPanorama) return;
    if (isEditingName) return;
    setLocalName(selectedPanorama.name || "");
  }, [selectedPanorama?.id, selectedPanorama?.name, isEditingName]);

  useEffect(() => {
    if (!selectedPanorama) return;
    const data =
      selectedPanorama.data && typeof selectedPanorama.data === "object"
        ? (selectedPanorama.data as Record<string, unknown>)
        : {};
    const minZoom = Number(data.minZoom ?? 0.2);
    const maxZoom = Number(data.maxZoom ?? 8);
    setMinZoomInput(Number.isFinite(minZoom) ? String(minZoom) : "0.2");
    setMaxZoomInput(Number.isFinite(maxZoom) ? String(maxZoom) : "8");
  }, [selectedPanorama?.id, selectedPanorama?.data]);

  useEffect(() => {
    setDefaultViewApproved(false);
    savedDefaultViewRef.current = null;
  }, [selectedPanorama?.id]);

  useEffect(() => {
    if (!defaultViewSaveEvent || !selectedPanorama) return;
    if (defaultViewSaveEvent.panoramaId !== selectedPanorama.id) return;

    savedDefaultViewRef.current = defaultViewSaveEvent.view;
    setDefaultViewApproved(true);
    setDefaultViewApprovedNonce(defaultViewSaveEvent.nonce);
  }, [defaultViewSaveEvent, selectedPanorama?.id]);

  const playDefaultViewClickSound = () => {
    const sound = clickSoundRef.current ?? new Audio("/sounds/click.mp3");
    clickSoundRef.current = sound;
    sound.currentTime = 0;
    sound.play().catch(() => {});
  };

  useEffect(() => {
    if (prevDefaultViewApprovedRef.current && !defaultViewApproved) {
      setCameraIconNonce(Date.now());
    }
    prevDefaultViewApprovedRef.current = defaultViewApproved;
  }, [defaultViewApproved]);

  useEffect(() => {
    if (!defaultViewApproved || !savedDefaultViewRef.current || !currentCameraView) return;

    const saved = savedDefaultViewRef.current;
    const yawDelta = Math.abs(currentCameraView.yaw - saved.yaw);
    const pitchDelta = Math.abs(currentCameraView.pitch - saved.pitch);
    const fovDelta = Math.abs(currentCameraView.fov - saved.fov);

    if (yawDelta > 0.25 || pitchDelta > 0.25 || fovDelta > 0.1) {
      setDefaultViewApproved(false);
    }
  }, [currentCameraView, defaultViewApproved]);

  if (!selectedPanorama) {
    return (
      <div className="h-full bg-gray-800 border-l border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-white mb-4">Media Properties</h2>
        <p className="text-gray-400">Select media to view its properties</p>
      </div>
    );
  }
  const mediaType = getPanoramaMediaType(selectedPanorama);
  const isZoomableImage = mediaType === "zoomable_image";

  const commitNameChange = () => {
    if (!selectedPanorama) return;
    const trimmed = localName.trim();
    if (!trimmed || trimmed === selectedPanorama.name) return;
    window.setTimeout(() => {
      updatePanoramaFromPanel(selectedPanorama.id, { name: trimmed });
    }, 0);
  };

  const handleGPSUpdate = (lat: number, lng: number) => {
    updatePanoramaFromPanel(selectedPanorama.id, {
      gps_location: { lat, lng },
    });
    setShowGPSPicker(false);
  };

  const handleViewDefaultView = () => {
    const panoramaData = selectedPanorama?.data as PanoramaDataRecord | undefined;
    const initialView = panoramaData?.initialView;

    if (!initialView || (initialView.yaw === undefined && initialView.pitch === undefined)) {
      toast({
        title: "No Default View",
        description:
          "This panorama doesn't have a default view set. Use 'Set Default View' to save the current camera position first.",
        variant: "destructive",
      });
      return;
    }

    const yaw = initialView.yaw ?? 0;
    const pitch = initialView.pitch ?? 0;
    const fov = initialView.fov ?? 75;

    // Dispatch a custom event to move the camera
    // The Viewer360 component will listen for this event
    const event = new CustomEvent("moveCameraToView", {
      detail: { yaw, pitch, fov },
    });
    window.dispatchEvent(event);

    toast({
      title: "Camera Moved",
      description: `Moved camera to default view: Yaw ${yaw.toFixed(1)}°, Pitch ${pitch.toFixed(1)}°, FOV ${fov.toFixed(1)}°`,
    });
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    if (
      (navigator.platform.includes("Mac") && e.metaKey) ||
      (!navigator.platform.includes("Mac") && e.ctrlKey)
    ) {
      // Move to trash before deleting DB record
      movePanoramaToTrashFromUrls(selectedPanorama, "User deleted from Properties Panel").catch(
        () => {},
      );
      deletePanoramaFromPanel(selectedPanorama.id);
    } else {
      setShowDeleteDialog(true);
    }
  };

  const handleUpdateData = (dataUpdates: PanoramaDataRecord) => {
    const currentData = selectedPanorama?.data || {};
    const dataToUpdate =
      typeof currentData === "object" && currentData !== null && !Array.isArray(currentData)
        ? currentData
        : {};

    updatePanoramaFromPanel(selectedPanorama.id, {
      data: { ...dataToUpdate, ...dataUpdates },
    });
  };

  const commitZoomValues = (target: "min" | "max") => {
    const currentData = getData();
    const currentMin = Number((currentData.minZoom as number | undefined) ?? 0.2);
    const currentMax = Number((currentData.maxZoom as number | undefined) ?? 8);

    const parsedMin = Number(minZoomInput);
    const parsedMax = Number(maxZoomInput);
    const nextMinRaw = Number.isFinite(parsedMin) ? parsedMin : currentMin;
    const nextMaxRaw = Number.isFinite(parsedMax) ? parsedMax : currentMax;

    const nextMin = Math.max(0.05, Math.min(nextMinRaw, nextMaxRaw - 0.01));
    const nextMax = Math.min(50, Math.max(nextMaxRaw, nextMin + 0.01));

    if (target === "min") {
      setMinZoomInput(String(nextMin));
      if (nextMax !== nextMaxRaw) setMaxZoomInput(String(nextMax));
    } else {
      setMaxZoomInput(String(nextMax));
      if (nextMin !== nextMinRaw) setMinZoomInput(String(nextMin));
    }

    handleUpdateData({ minZoom: nextMin, maxZoom: nextMax });
  };

  const handleSetCurrentViewAsInitial = async () => {
    if (!selectedPanorama || !currentCameraView) {
      toast({
        variant: "destructive",
        title: "No camera view available",
        description:
          "Please move the camera to the desired position before setting the initial view.",
      });
      return;
    }

    const initialView = {
      yaw: currentCameraView.yaw,
      pitch: currentCameraView.pitch,
      fov: currentCameraView.fov,
    };

    // When multiple panoramas are selected, apply to all of them
    const targetIds =
      selectedPanoramaIds.length > 1 ? selectedPanoramaIds : [selectedPanorama.id];

    try {
      await Promise.all(
        targetIds.map(async (panoramaId) => {
          const panorama = panoramas.find((p) => p.id === panoramaId);
          if (!panorama) return;
          const updatedData = { ...(panorama.data || {}), initialView };
          await updatePanoramaFromPanel(panoramaId, { data: updatedData });
        }),
      );

      setDefaultViewSaveEvent({
        panoramaId: selectedPanorama.id,
        view: initialView,
        nonce: Date.now(),
      });
      playDefaultViewClickSound();
    } catch (error) {
      console.error("Error setting default view:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set default view.",
      });
    }
  };

  const handleSetNorth = async () => {
    if (!selectedPanorama || !currentCameraView) {
      toast({
        variant: "destructive",
        title: "No camera view available",
        description: "Please move the camera to point north before setting north.",
      });
      return;
    }

    try {
      const updatedData = {
        ...(selectedPanorama.data || {}),
        north: currentCameraView.yaw,
      };

      await updatePanoramaFromPanel(selectedPanorama.id, {
        data: updatedData,
      });

      // Reposition existing navigation hotspots
      const repositioned = await repositionHotspotsForNorthChange(
        { ...selectedPanorama, data: updatedData },
        hotspots,
        panoramas,
      );

      // Refresh hotspots from database
      const hotspotsData = await convex.query(api.hotspots.listByProject, {
        projectId: selectedPanorama.project_id,
      });
      if (hotspotsData) {
        setHotspots(hotspotsData);
      }

      toast({
        title: "North set",
        description: `North has been set to ${currentCameraView.yaw.toFixed(1)}° for ${selectedPanorama.name}. ${repositioned > 0 ? `${repositioned} navigation hotspot${repositioned > 1 ? "s" : ""} repositioned.` : "No navigation hotspots to reposition."}`,
      });
    } catch (error) {
      console.error("Error setting north:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set north.",
      });
    }
  };

  // Helper to safely extract data properties
  const getData = () => {
    const data = selectedPanorama?.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return data as PanoramaDataRecord;
    }
    return {};
  };

  const gpsLocation = selectedPanorama?.gps_location as { lat: number; lng: number } | null;
  return (
    <div className="h-full bg-gray-800 border-l border-gray-700 flex flex-col">
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">
          {isZoomableImage ? "Zoomable Image Properties" : "Panorama Properties"}
        </h2>
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogTrigger asChild>
            <button
              onClick={handleDeleteClick}
              className="hover:bg-gray-700 p-1 rounded text-gray-300 hover:text-red-400 transition-colors"
              title="Delete panorama"
              type="button"
            >
              <Trash2 size={16} />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 max-w-sm w-full">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Panorama?</DialogTitle>
              <DialogDescription className="text-gray-400">
                This action cannot be undone. The panorama will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="text-gray-300 mb-4">Are you sure you want to delete this panorama?</div>
            <div className="text-xs text-gray-400 mb-4">
              Tip: Hold{" "}
              <span className="font-semibold">
                {navigator.platform.includes("Mac") ? "⌘ Command" : "Ctrl"}
              </span>{" "}
              and click delete to skip this confirmation.
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                autoFocus
                onClick={() => {
                  setShowDeleteDialog(false);
                  movePanoramaToTrashFromUrls(
                    selectedPanorama,
                    "User deleted from Properties Panel",
                  ).catch(() => {});
                  deletePanoramaFromPanel(selectedPanorama.id);
                }}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-1 bg-gray-700">
              <TabsTrigger
                value="properties"
                className="data-[state=active]:bg-gray-600 data-[state=active]:text-white text-gray-300"
              >
                Properties
              </TabsTrigger>
            </TabsList>

            <TabsContent value="properties" className="space-y-4 mt-4">
              {/* Basic Properties */}
              <Card className="bg-gray-700 border-gray-600">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-xs">Basic Properties</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="panorama-name" className="text-gray-300 text-xs">
                      Name
                    </Label>
                    <Input
                      id="panorama-name"
                      value={localName}
                      onFocus={() => setIsEditingName(true)}
                      onChange={(e) => handleNameChange(e.target.value)}
                      onBlur={() => {
                        setIsEditingName(false);
                        commitNameChange();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setIsEditingName(false);
                          commitNameChange();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setIsEditingName(false);
                          setLocalName(selectedPanorama?.name || "");
                        }
                      }}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  {showDebug && (
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Panorama ID</Label>
                      <div className="text-gray-300 text-xs font-mono bg-gray-800 px-2 py-1 rounded">
                        {selectedPanorama?.id || "N/A"}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-gray-300 text-xs">Dimensions</Label>
                    <div className="text-gray-300 text-xs">
                      {selectedPanorama?.width} × {selectedPanorama?.height} px
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300 text-xs">File Sizes</Label>
                    <div className="space-y-2">
                      {/* Original File Size - Only show if original file URL exists */}
                      {(() => {
                        const data = selectedPanorama?.data as PanoramaDataRecord | undefined;
                        const hasOriginalUrl = !!data?.original_file_url;

                        if (!hasOriginalUrl) return null;

                        return (
                          <button
                            className="flex items-center justify-between p-2 rounded cursor-pointer transition-colors bg-gray-600 hover:bg-gray-500 w-full text-left"
                            onClick={async () => {
                              const currentData = getData();
                              console.log("🔄 Switching to original file");

                              // Update the panorama data immediately
                              await updatePanoramaFromPanel(selectedPanorama.id, {
                                data: { ...currentData, useOriginalFile: true },
                              });

                              // Force refresh the viewer by updating the selected panorama
                              const updatedPanorama = {
                                ...selectedPanorama,
                                data: { ...currentData, useOriginalFile: true },
                              };
                              useEditorStore.getState().setSelectedPanorama(updatedPanorama);
                            }}
                            type="button"
                          >
                            <div>
                              <div className="text-xs font-medium text-gray-300">Original</div>
                              <div className="text-xs text-gray-400">Backup file</div>
                            </div>
                            <div className="text-xs text-gray-300">
                              {selectedPanorama?.file_size
                                ? `${(selectedPanorama.file_size / 1024 / 1024).toFixed(2)} MB`
                                : "Unknown"}
                            </div>
                          </button>
                        );
                      })()}

                      {/* Compression Info - Show for both scenarios: original+compressed OR compressed-only with stored data */}
                      {(() => {
                        const data = selectedPanorama?.data as PanoramaDataRecord | undefined;
                        const hasOriginalUrl = !!data?.original_file_url;
                        const url =
                          selectedPanorama?.private_file_url || selectedPanorama?.file_url;
                        const isCompressed = url?.includes("compressed_") || url?.includes(".webp");

                        // Show compression info if:
                        // 1. We have both original and compressed files, OR
                        // 2. We have compressed file with stored compression data (compressed-only upload)
                        if (
                          data?.compression_ratio &&
                          (hasOriginalUrl || (isCompressed && data?.original_file_size))
                        ) {
                          const originalSize = data.original_file_size;
                          const compressedSize =
                            data.compressed_file_size || selectedPanorama?.file_size;
                          const savings = `${((1 - data.compression_ratio) * 100).toFixed(1)}%`;
                          const originalSizeMB = `${(originalSize / 1024 / 1024).toFixed(2)} MB`;
                          const compressedSizeMB = `${(compressedSize / 1024 / 1024).toFixed(2)} MB`;

                          return (
                            <div className="space-y-1">
                              <div className="text-gray-300 text-xs">
                                {compressedSizeMB} (original {originalSizeMB})
                              </div>
                              <div className="text-gray-400 text-xs">Size reduced by {savings}</div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div className="w-full">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById("file-upload")?.click()}
                      className="w-full min-w-0 bg-gray-600 border-gray-500 text-white hover:bg-gray-500 gap-1.5"
                    >
                      <Upload className="w-4 h-4" />
                      {isZoomableImage ? "Replace zoomable image" : "Replace panorama"}
                    </Button>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </div>
                </CardContent>
              </Card>

              {isZoomableImage && (
                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-xs">Zoom Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Min Zoom</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.05"
                        max="50"
                        value={minZoomInput}
                        onChange={(e) => setMinZoomInput(e.target.value)}
                        onBlur={() => commitZoomValues("min")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitZoomValues("min");
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        className="bg-gray-600 border-gray-500 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Max Zoom</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.05"
                        max="50"
                        value={maxZoomInput}
                        onChange={(e) => setMaxZoomInput(e.target.value)}
                        onBlur={() => commitZoomValues("max")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitZoomValues("max");
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        className="bg-gray-600 border-gray-500 text-white"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* TODO: Live Camera Stats - now displayed in viewer overlay (top-right) */}

              {/* Default View Stats */}
              {!isZoomableImage && <Card className="bg-gray-700 border-gray-600">
                <CardHeader className="px-3 pt-3 pb-2">
                  <CardTitle className="text-white text-xs flex items-center justify-between">
                    <span>🏠 Default View Stats</span>
                    {selectedPanoramaIds.length > 1 && (
                      <span className="text-[10px] font-medium bg-blue-600 text-white px-1.5 py-0.5 rounded-full">
                        {selectedPanoramaIds.length} selected
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5 px-3 pb-3 pt-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSetCurrentViewAsInitial}
                    className="bg-gray-600 border-gray-500 text-white hover:bg-gray-500 w-full gap-1.5 h-10"
                  >
                    {defaultViewApproved ? (
                      <Check
                        key={defaultViewApprovedNonce}
                        className="w-4 h-4 text-emerald-300"
                        style={{ animation: "default-view-check-pop 220ms ease-out" }}
                      />
                    ) : (
                      <Camera
                        key={cameraIconNonce}
                        className="w-4 h-4"
                        style={
                          cameraIconNonce
                            ? { animation: "default-view-camera-pop 180ms ease-out" }
                            : undefined
                        }
                      />
                    )}
                    Set Default View
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleViewDefaultView}
                    className="bg-gray-600 border-gray-500 text-white hover:bg-gray-500 w-full gap-1.5 h-10"
                  >
                    <ArrowRight className="w-4 h-4" />
                    View Default
                  </Button>

                  <Separator className="bg-gray-700" />

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSetNorth}
                    className="bg-gray-600 border-gray-500 text-white hover:bg-gray-500 w-full gap-1.5 h-10"
                  >
                    <Navigation className="w-4 h-4" />
                    Set North
                  </Button>

                  {(() => {
                    const panoramaData = selectedPanorama?.data as PanoramaDataRecord | undefined;
                    const north = panoramaData?.north;

                    if (north !== undefined && north !== null) {
                      return (
                        <div className="text-center text-xs">
                          <div className="text-gray-400">North Offset</div>
                          <div className="text-blue-300 font-bold">{north.toFixed(1)}°</div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-center text-gray-400 text-xs py-2">
                          No north set. Point camera north and click "Set North".
                        </div>
                      );
                    }
                  })()}

                  {(() => {
                    const panoramaData = selectedPanorama?.data as PanoramaDataRecord | undefined;
                    const initialView = panoramaData?.initialView;

                    // Check if initial view exists with the new format (yaw, pitch, fov)
                    if (
                      initialView &&
                      (initialView.yaw !== undefined || initialView.pitch !== undefined)
                    ) {
                      const yaw = initialView.yaw ?? 0;
                      const pitch = initialView.pitch ?? 0;
                      const fov = initialView.fov ?? 75;

                      return (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center">
                            <div className="text-gray-400">Yaw</div>
                            <div className="text-yellow-300 font-bold">{yaw.toFixed(1)}°</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-400">Pitch</div>
                            <div className="text-green-300 font-bold">{pitch.toFixed(1)}°</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-400">FOV</div>
                            <div className="text-purple-300 font-bold">{fov.toFixed(1)}°</div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-center text-gray-400 text-xs py-4">
                          <div>No default view set for this panorama</div>
                          <div className="mt-2 text-gray-500">
                            Click "Set Default View" above to save the current camera position.
                          </div>
                        </div>
                      );
                    }
                  })()}
                </CardContent>
              </Card>}

              {/* GPS Location */}
              <Card className="bg-gray-700 border-gray-600">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-xs flex items-center justify-between">
                    <span>GPS Location</span>
                    {gpsLocation && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const event = new CustomEvent("openMapOverlay", {
                            detail: { panoramaId: selectedPanorama.id },
                          });
                          window.dispatchEvent(event);
                        }}
                        className="h-6 w-6 p-0 bg-gray-600 hover:bg-gray-500 text-white"
                        title="Open map"
                      >
                        <MapPin className="w-4 h-4" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {gpsLocation ? (
                    <div className="space-y-2">
                      <div className="text-gray-300 text-xs">
                        Latitude: {gpsLocation.lat?.toFixed(6) || "N/A"}
                      </div>
                      <div className="text-gray-300 text-xs">
                        Longitude: {gpsLocation.lng?.toFixed(6) || "N/A"}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-xs">No GPS data available</div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowGPSPicker(true)}
                    className="w-full bg-gray-600 border-gray-500 text-white hover:bg-gray-500"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {gpsLocation ? "Update Location" : "Set Location"}
                  </Button>
                </CardContent>
              </Card>
              {/* Version History */}
              <VersionHistory panoramaId={selectedPanorama.id} />
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* GPS Location Picker Modal */}
      {showGPSPicker && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-3/4 h-3/4 max-w-4xl max-h-[600px]">
            <GPSLocationPicker
              initialLatitude={gpsLocation?.lat || 0}
              initialLongitude={gpsLocation?.lng || 0}
              onLocationSelect={handleGPSUpdate}
            />
            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGPSPicker(false)}
                className="bg-gray-600 border-gray-500 text-white hover:bg-gray-500"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
